import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { environment } from './../../../environments/environment';
import {
  CompletedEvent,
  DateRange,
  EventInterface,
  GameInfo,
  GameMessage,
  ScheduledEvent,
  TodayEvent,
  TriviaGame,
} from '@core/models';
import moment from 'moment-timezone';
import { FirebaseService } from '@core/services/firebase.service';
import { TrebekTriviaGame } from '@core/models/events/trebek-trivia-game.interface';
import * as modifyGameHelper from '@core/helpers/modify-game.helper';

@Injectable({
  providedIn: 'root',
})
export class EventsService {
  constructor(
    private http: HttpClient,
    private firebaseService: FirebaseService,
  ) {}

  public getTodayEvents(
    offset: number = 0,
    limit: number = 10,
  ): Observable<TodayEvent> {
    const data = {
      start_datetime: moment().startOf('day').utc(false).format(),
      end_datetime: moment().endOf('day').utc(false).format(),
    };
    return this.http.post<TodayEvent>(
      `${environment.apiUrl}events/today-events?offset=${offset}&limit=${limit}`,
      data,
    );
  }

  public getScheduleEvents(
    range: DateRange,
    offset: number = 0,
    limit: number = 10,
  ): Observable<ScheduledEvent> {
    const data = {
      start_datetime: moment(range.start).startOf('day').utc(false).format(),
      end_datetime: moment(range.end).endOf('day').utc(false).format(),
    };
    return this.http.post<ScheduledEvent>(
      `${environment.apiUrl}events/schedule-events?offset=${offset}&limit=${limit}`,
      data,
    );
  }

  public getCompletedEvents(
    range,
    offset: number = 0,
    limit: number = 10,
  ): Observable<CompletedEvent> {
    return this.http
      .post<CompletedEvent>(
        `${environment.apiUrl}events/completed-events?offset=${offset}&limit=${limit}`,
        range,
      )
      .pipe(
        switchMap((events) => {
          const ids = events.completed.map((e) => e.event_id);
          return this.firebaseService.getEventsInfo(ids).pipe(
            map((info: any) => {
              info?.forEach((i) => {
                const completed = events.completed.find(
                  (e) => e.event_id === i.id,
                );
                if (!completed) return;
                completed.player_count =
                  completed.player_count === 0
                    ? i.player_count
                    : completed.player_count;
                completed.team_count =
                  completed.team_count === 0
                    ? i.team_count
                    : completed.team_count;
              });
              return events;
            }),
          );
        }),
      );
  }

  public getGameMessage(eventId: string): Observable<GameMessage> {
    return this.http.get<GameMessage>(
      `${environment.apiUrl}gameplay/message/${eventId}`,
    );
  }

  public saveGameMessage(
    eventId: string,
    data: { host_notes: string; host_recap: string },
  ): Observable<GameMessage> {
    return this.http.post<GameMessage>(
      `${environment.apiUrl}gameplay/save-message/${eventId}`,
      data,
    );
  }

  public submitGameMessage(
    eventId: string,
    data: { host_notes: string; host_recap: string },
  ): Observable<GameMessage> {
    return this.http.post<GameMessage>(
      `${environment.apiUrl}gameplay/submit-message/${eventId}`,
      data,
    );
  }

  public getGameInfo(eventId: string): Observable<GameInfo> {
    return forkJoin([
      this.http.get<GameInfo>(
        `${environment.apiUrl}gameplay/gameinfo/${eventId}`,
      ),
      this.http.get<EventInterface>(`${environment.apiUrl}events/${eventId}`),
    ]).pipe(
      map(([gameInfo, event]) => {
        return {
          ...gameInfo,
          location_id: event.location_id,
          host_id: event.user_id,
        };
      }),
    );
  }

  public getGame(
    gameData: GameInfo,
    gameId: string,
  ): Observable<TriviaGame | null> {
    const gameCode = String(gameData.game_code);
    return this.firebaseService.getGame(gameCode).pipe(
      switchMap((game) => {
        if (!game) {
          const gameFile = gameId
            ? this.getGameById(gameId)
            : this.getDailyGame(gameData.game_format_id);
          return gameFile.pipe(
            switchMap((dailyGame) => {
              game = modifyGameHelper.modifyFunctionStructure(dailyGame);
              game.trivia_game.start_datetime = gameData.start_datetime;
              return this.firebaseService.createGame(game, gameCode);
            }),
          );
        }
        return of(game);
      }),
    );
  }

  private getDailyGame(
    gameFormatId: number | undefined,
  ): Observable<TrebekTriviaGame> {
    const currentDate = moment().format('YYYY-MM-DD');
    return this.http
      .get<any>(
        `${environment.trebekTriviaUrl}games/game-by-date?date=${currentDate}&gameFormat=${gameFormatId}`,
      )
      .pipe(map((game) => game.trivia_events[0].trivia_game));
  }

  private getGameById(gameId: string): Observable<TrebekTriviaGame> {
    const data = {
      action: 'f_trivia_game_by_id',
      data: {
        game_id: gameId,
      },
    };
    return this.http
      .post<any>(`${environment.trebekDoActionUrl}`, data)
      .pipe(map((res) => res.trivia_games[0]));
  }
}
